// Login page — PRD-01 US-AUTH-01 + PRD-03 (mobile-first, B1-taalniveau).

import { useState, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { authStore, type CurrentUser } from "@/lib/auth";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: CurrentUser;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Geen "demo" als beginwaarde. Een echte gebruiker van een gemeente krijgt
  // dan de naam van een vreemde organisatie voorgeschoteld, en het verraadt
  // bovendien dat die tenant bestaat. Wie hier eerder inlogde krijgt zijn eigen
  // slug terug; de rest ziet een leeg veld met uitleg eronder.
  const [tenantSlug, setTenantSlug] = useState(authStore.getTenantSlug() ?? "");
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [digidAvailable, setDigidAvailable] = useState(false);

  // Feature-flag probe: backend geeft 503 ERR_DIGID_NOT_CONFIGURED als env niet gezet.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/auth/digid/status")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j) return;
        const data = (j?.data ?? j) as { enabled?: boolean; configured?: boolean } | undefined;
        if (data?.enabled && data?.configured) setDigidAvailable(true);
      })
      .catch(() => {
        /* graceful degrade */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorKey(null);
    setLoading(true);
    try {
      const data = await api.post<LoginResponse>(
        "/v1/auth/login",
        { email, password, tenantSlug },
        { skipAuth: true, tenantSlug }
      );
      authStore.setSession(data.accessToken, data.refreshToken, data.user, tenantSlug);
      const role = data.user.role;
      const target =
        role === "admin" || role === "super_admin"
          ? "/admin/users"
          : role === "counselor"
            ? "/counselor"
            : role === "municipality"
              ? "/municipality"
              : "/dashboard";
      // Hard-reload navigatie (window.location): useSyncExternalStore-snapshots
      // propageren niet altijd snel genoeg vóór ProtectedRoute's volgende render,
      // wat tot redirect-loop /dashboard → /login leidde. Een full page-load
      // garandeert dat localStorage de single source of truth is.
      window.location.replace(target);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ERR_AUTH_MFA_REQUIRED") {
        const details = err.details as
          | { mfaChallenge?: string; mustEnroll?: boolean }
          | undefined;
        if (details?.mfaChallenge) {
          navigate("/mfa", {
            state: { mfaChallenge: details.mfaChallenge, mustEnroll: details.mustEnroll, tenantSlug },
          });
          return;
        }
        setErrorKey("errors.internal");
      } else if (err instanceof ApiError) {
        setErrorKey(err.i18nKey);
      } else {
        setErrorKey("errors.internal");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <LocaleSwitcher />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
          <h1 className="text-2xl font-semibold text-neutral-900 mb-1">
            {t("auth.login.title", "Inloggen bij Balans Buddy")}
          </h1>
          <p className="text-sm text-neutral-600 mb-6">
            {t("auth.login.subtitle", "Welkom terug. Vul je gegevens in om verder te gaan.")}
          </p>

          <form onSubmit={onSubmit} className="space-y-4" aria-busy={loading}>
            <div>
              <label htmlFor="tenantSlug" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("auth.login.tenant", "Organisatie")}
              </label>
              <input
                id="tenantSlug"
                type="text"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                required
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                autoComplete="organization"
                placeholder={t("auth.login.tenantPlaceholder", "bijv. amsterdam")}
                aria-describedby="tenantSlug-help"
              />
              <p id="tenantSlug-help" className="mt-1 text-xs text-neutral-500">
                {t(
                  "auth.login.tenantHelp",
                  "De korte naam van je organisatie. Weet je hem niet? Vraag het je beheerder."
                )}
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("auth.login.email", "E-mail")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("auth.login.password", "Wachtwoord")}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {errorKey && (
              <div
                role="alert"
                className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
              >
                {t(errorKey, t("errors.internal", "Er ging iets mis"))}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {loading ? t("auth.login.loading", "Bezig…") : t("auth.login.submit", "Inloggen")}
            </button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-brand-600 hover:text-brand-700">
              {t("auth.login.forgot", "Wachtwoord vergeten?")}
            </Link>
          </p>

          {digidAvailable && (
            <div className="mt-6">
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-neutral-500">
                    {t("auth.login.or", "of")}
                  </span>
                </div>
              </div>
              <a
                href={`/api/v1/auth/digid/login?tenantSlug=${encodeURIComponent(tenantSlug)}`}
                className="block w-full text-center px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
              >
                {t("auth.login.digid", "DigiD inloggen")}
              </a>
            </div>
          )}

          <p className="mt-6 text-xs text-center text-neutral-500">
            {t("auth.login.legal", "Beveiligde verbinding · JWT + MFA · AVG-compliant")}
          </p>
        </div>
      </div>
    </main>
  );
}
